/**
 * authResolved — the web-side response to a native `swimset:auth-resolved` nudge
 * (G2). Native fires this event the moment the first-launch HealthKit permission
 * sheet is dismissed (grant OR deny — read-auth status is unobservable). It is a
 * one-per-launch recovery for the two surfaces the auth race can blank PAST
 * Task 3's bounded retry window: the workout LIST (a one-shot fetch) and the
 * current swim's firehose. Pure + injectable so it is unit-tested.
 */
import type { FirehoseStatus } from "./state";

/** The current swim's firehose is only worth refetching if the last fetch did
 *  NOT yield a swim: "empty" (auth race returned no data) or "error" (e.g. the
 *  5s bridge timeout). "ready" needs no refetch — the nudge stays a no-op, so it
 *  never re-publishes a swim that already loaded. "loading" means a fetch (or the
 *  Task 3 retry) is still in flight; leave it alone. */
export function shouldRefetchFirehose(status: FirehoseStatus): boolean {
  return status === "empty" || status === "error";
}

export interface AuthResolvedHandlers {
  /** Re-run the workout-list fetch past its one-shot guard. */
  refetchWorkouts: () => void | Promise<void>;
  /** Read the current firehose fetch lifecycle. */
  firehoseStatus: () => FirehoseStatus;
  /** Re-run the current swim's firehose fetch effect. */
  refetchFirehose: () => void;
}

/** Handle one native auth-resolved nudge: ALWAYS refetch the workout list (its
 *  one-shot guard blocked any auth-race retry), and refetch the current firehose
 *  only if it previously came back empty or errored. Idempotent — safe to call
 *  after a successful load. */
export function handleAuthResolved(h: AuthResolvedHandlers): void {
  void h.refetchWorkouts();
  if (shouldRefetchFirehose(h.firehoseStatus())) {
    h.refetchFirehose();
  }
}
